import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ResearchShell from '../components/research/ResearchShell'
import AnalysisResultView from '../components/research/AnalysisResultView'
import { SECTORS, type OpusAnalysisResponse, type ResearchTaskType } from '../types/research'
import { analyzeWithOpus, isResearchUsingMockData, saveIdea } from '../lib/research-api'

const TASKS: { id: ResearchTaskType; label: string; icon: string; blurb: string }[] = [
  {
    id: 'analyze-filing',
    label: 'Analyze a filing',
    icon: '📄',
    blurb: 'Break down the latest filing for a company — offerings, risk factors, liquidity.',
  },
  {
    id: 'analyze-security',
    label: 'Analyze a security',
    icon: '📈',
    blurb: 'Full work-up on one name: filings, options chain, fundamentals, and vol picture.',
  },
  {
    id: 'scan-offerings',
    label: 'Scan security offerings',
    icon: '🧾',
    blurb: 'Sweep a sector or list for convertible debt, preferred conversion clauses, and shelf capacity.',
  },
  {
    id: 'flag-thesis',
    label: 'Flag by thesis',
    icon: '🚩',
    blurb: 'Give a thesis plus a sector or list — Opus flags securities matching it on short-term vol.',
  },
  {
    id: 'match-position',
    label: 'Match a position type',
    icon: '🎯',
    blurb: 'Describe a position (e.g. short-term bonds) and get securities matching that profile.',
  },
]

type Scope = 'sector' | 'list' | 'all'

export default function ResearchPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [task, setTask] = useState<ResearchTaskType>('analyze-security')
  const [prompt, setPrompt] = useState('')
  const [symbol, setSymbol] = useState('')
  const [symbolsText, setSymbolsText] = useState('')
  const [sector, setSector] = useState<string>(SECTORS[0])
  const [scope, setScope] = useState<Scope>('sector')
  const [minDivergence, setMinDivergence] = useState(5)
  const [positionType, setPositionType] = useState('')
  const [includeOptions, setIncludeOptions] = useState(true)
  const [includeFundamentals, setIncludeFundamentals] = useState(true)

  const [running, setRunning] = useState(false)
  const [history, setHistory] = useState<OpusAnalysisResponse[]>([])
  const [isMock, setIsMock] = useState(false)
  const [trackedId, setTrackedId] = useState<string | null>(null)
  const resultRef = useRef<HTMLDivElement | null>(null)

  // Prefill from query params (deep links from Screener / Ideas / Filings)
  useEffect(() => {
    const t = searchParams.get('task') as ResearchTaskType | null
    if (t && TASKS.some((x) => x.id === t)) setTask(t)
    const sym = searchParams.get('symbol')
    if (sym) setSymbol(sym.toUpperCase())
    const syms = searchParams.get('symbols')
    if (syms) {
      setSymbolsText(syms.split(',').join(', '))
      setScope('list')
    }
    const thesis = searchParams.get('thesis')
    if (thesis) setPrompt(thesis)
    const sec = searchParams.get('sector')
    if (sec) {
      setSector(sec)
      setScope('sector')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const activeTask = TASKS.find((t) => t.id === task)!

  const usesScope = task === 'scan-offerings' || task === 'flag-thesis' || task === 'match-position'
  const canRun =
    task === 'analyze-filing' || task === 'analyze-security'
      ? symbol.trim().length > 0
      : task === 'match-position'
        ? positionType.trim().length > 0
        : true

  async function run() {
    if (!canRun || running) return
    setRunning(true)
    setTrackedId(null)
    try {
      const symbols = symbolsText
        .split(/[,\s]+/)
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean)
      const result = await analyzeWithOpus({
        task,
        prompt: prompt.trim() || undefined,
        symbol: symbol.trim() ? symbol.trim().toUpperCase() : undefined,
        symbols: usesScope && scope === 'list' && symbols.length ? symbols : undefined,
        sector: usesScope && scope === 'sector' ? sector : undefined,
        minDivergence: task === 'flag-thesis' ? minDivergence : undefined,
        positionType: task === 'match-position' ? positionType.trim() : undefined,
        includeOptionsChain: task === 'analyze-security' ? includeOptions : undefined,
        includeFundamentals: task === 'analyze-security' ? includeFundamentals : undefined,
      })
      setHistory((prev) => [result, ...prev])
      setIsMock(isResearchUsingMockData())
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50)
    } finally {
      setRunning(false)
    }
  }

  async function trackFlagged(result: OpusAnalysisResponse) {
    const idea = await saveIdea({
      title:
        task === 'match-position'
          ? `Position match: ${positionType || 'unspecified'}`
          : `Opus flags: ${prompt.slice(0, 60) || activeTask.label}`,
      thesis: prompt,
      status: 'watching',
      symbols: result.flagged.map((f) => f.symbol),
      tags: ['opus'],
      notes: `Generated by ${result.model} on ${new Date(result.createdAt).toLocaleDateString()}`,
      source: 'opus-flag',
    })
    setTrackedId(idea.id)
  }

  const inputCls =
    'w-full bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white text-sm rounded-lg px-3 py-2 border border-slate-300 dark:border-slate-600 focus:border-indigo-500 outline-none placeholder-slate-400 dark:placeholder-slate-500'
  const labelCls = 'text-xs text-slate-500 dark:text-slate-400'

  return (
    <ResearchShell showMockBanner={isMock && !running}>
      {/* Task picker */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 mb-5">
        {TASKS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTask(t.id)}
            className={`text-left p-3 rounded-xl border transition-colors ${
              task === t.id
                ? 'bg-indigo-500/10 border-indigo-500/50'
                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500'
            }`}
          >
            <div className="text-lg mb-1">{t.icon}</div>
            <div className={`text-sm font-medium ${task === t.id ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-900 dark:text-white'}`}>
              {t.label}
            </div>
          </button>
        ))}
      </div>

      {/* Task form */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 mb-6">
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{activeTask.blurb}</p>

        <div className="space-y-3">
          {(task === 'analyze-filing' || task === 'analyze-security') && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="block sm:col-span-1">
                <span className={labelCls}>Ticker symbol</span>
                <input
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                  placeholder="e.g. AAPL"
                  className={`mt-1 ${inputCls}`}
                />
              </label>
              {task === 'analyze-security' && (
                <div className="flex items-end gap-5 pb-2 sm:col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={includeOptions}
                      onChange={(e) => setIncludeOptions(e.target.checked)}
                      className="w-4 h-4 accent-indigo-600"
                    />
                    <span className="text-sm text-slate-600 dark:text-slate-300">Options chain</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={includeFundamentals}
                      onChange={(e) => setIncludeFundamentals(e.target.checked)}
                      className="w-4 h-4 accent-indigo-600"
                    />
                    <span className="text-sm text-slate-600 dark:text-slate-300">Fundamentals</span>
                  </label>
                </div>
              )}
            </div>
          )}

          {task === 'match-position' && (
            <label className="block">
              <span className={labelCls}>Position type</span>
              <input
                value={positionType}
                onChange={(e) => setPositionType(e.target.value)}
                placeholder="e.g. short-term bonds, convertible debt, preferred equity"
                className={`mt-1 ${inputCls}`}
              />
            </label>
          )}

          {usesScope && (
            <div>
              <span className={labelCls}>Search scope</span>
              <div className="flex gap-1.5 mt-1 mb-2">
                {(['sector', 'list', 'all'] as Scope[]).map((s) => (
                  <button
                    key={s}
                    onClick={() => setScope(s)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                      scope === s
                        ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-slate-900/50'
                    }`}
                  >
                    {s === 'list' ? 'Symbol list' : s === 'all' ? 'Whole universe' : 'Sector'}
                  </button>
                ))}
              </div>
              {scope === 'sector' && (
                <select value={sector} onChange={(e) => setSector(e.target.value)} className={inputCls}>
                  {SECTORS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              )}
              {scope === 'list' && (
                <input
                  value={symbolsText}
                  onChange={(e) => setSymbolsText(e.target.value)}
                  placeholder="Symbols — comma separated (e.g. NVDA, TSLA, MRNA)"
                  className={inputCls}
                />
              )}
            </div>
          )}

          {task === 'flag-thesis' && (
            <label className="block">
              <span className={labelCls}>Min short-term vol divergence (vol points)</span>
              <input
                type="number"
                min={0}
                max={30}
                value={minDivergence}
                onChange={(e) => setMinDivergence(Number(e.target.value))}
                className={`mt-1 sm:w-56 ${inputCls}`}
              />
            </label>
          )}

          <label className="block">
            <span className={labelCls}>
              {task === 'flag-thesis'
                ? 'Investment thesis'
                : task === 'scan-offerings'
                  ? 'Focus (optional) — e.g. "convertible debt conversion clauses"'
                  : 'Question or instructions (optional)'}
            </span>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={task === 'flag-thesis' ? 3 : 2}
              placeholder={
                task === 'flag-thesis'
                  ? 'e.g. Names where options are pricing far more short-term movement than realized — potential premium-selling candidates into earnings.'
                  : 'e.g. Summarize the security offerings and any conversion clauses.'
              }
              className={`mt-1 ${inputCls}`}
            />
          </label>

          <button
            onClick={run}
            disabled={!canRun || running}
            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            {running ? 'Opus is working…' : `Run with Opus 5`}
          </button>
        </div>
      </div>

      {/* Running indicator */}
      {running && (
        <div className="bg-white dark:bg-slate-800 border border-indigo-500/30 rounded-xl p-6 mb-6 flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-slate-300 dark:border-slate-600 border-t-indigo-500 rounded-full animate-spin" />
          <div>
            <p className="text-sm font-medium text-slate-900 dark:text-white">Opus 5 is analyzing…</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Pulling filings, options data, and fundamentals for the requested scope
            </p>
          </div>
        </div>
      )}

      {/* Results */}
      <div ref={resultRef} className="space-y-6">
        {history.map((result, idx) => (
          <div key={result.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
            {idx === 0 && result.flagged.length > 0 && (
              <div className="flex justify-end mb-2">
                {trackedId ? (
                  <button
                    onClick={() => navigate('/dashboard/financial-app/ideas')}
                    className="px-3 py-1.5 text-xs bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-lg"
                  >
                    ✓ Tracked — view in Ideas
                  </button>
                ) : (
                  <button
                    onClick={() => trackFlagged(result)}
                    className="px-3 py-1.5 text-xs bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded-lg transition-colors"
                  >
                    💡 Track flagged as idea
                  </button>
                )}
              </div>
            )}
            <AnalysisResultView result={result} />
          </div>
        ))}
      </div>

      {!running && history.length === 0 && (
        <div className="text-center py-14 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl">
          <div className="text-4xl mb-3">🧠</div>
          <p className="text-slate-900 dark:text-white font-medium">Opus 5 research assistant</p>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1 max-w-lg mx-auto">
            Pick a task above — analyze filings, sweep sectors for convertible and preferred
            structures, flag securities against a thesis, or find names matching a position type.
            Flagged results can be tracked as ideas with one click.
          </p>
        </div>
      )}
    </ResearchShell>
  )
}
